import {VIDEO_PLAY_STATES} from '@/consts/video/videoPlayStates';

const guideVideoUtils = ({videoRef, playState, startTime, stopTime}) => {
    const video = videoRef.current;

    const getVideoCurrentTime = () => videoRef.current?.currentTime;

    const getVideoEndTime = () => stopTime || videoRef.current?.duration || 0;

    const getVideoPlayState = () => {
        if (!videoRef.current) {
            return playState;
        }

        const isPaused = videoRef.current.paused;
        const isPlaying = !isPaused;

        if (isPlaying) {
            return VIDEO_PLAY_STATES.PLAYING;
        }

        const isAtStart = videoRef.current.currentTime === 0;

        if (isPaused && isAtStart) {
            return VIDEO_PLAY_STATES.STOPPED;
        }

        const isAtEnd = videoRef.current.currentTime >= getVideoEndTime(video);

        if (isPaused && isAtEnd) {
            return VIDEO_PLAY_STATES.ENDED;
        }

        if (isPaused) {
            return VIDEO_PLAY_STATES.PAUSED;
        }

        return playState;
    };

    const resetVideoToStart = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = startTime;
        }
    };

    const resetVideoToEnd = () => {
        if (videoRef.current) {
            const endTime = getVideoEndTime(videoRef);

            videoRef.current.currentTime = endTime;
        }
    };

    const playVideo = () => {
        if (videoRef.current) {
            const playState = getVideoPlayState();

            if (playState !== VIDEO_PLAY_STATES.PLAYING) {
                if (playState === VIDEO_PLAY_STATES.ENDED) {
                    resetVideoToStart();
                }

                videoRef.current.play().catch(() => {}); // catch is necessary
            }
        }
    };

    const pauseVideo = () => {
        if (videoRef.current) {
            const playState = getVideoPlayState();

            if (playState === VIDEO_PLAY_STATES.PLAYING) {
                videoRef.current.pause();
            }
        }
    };

    const stopVideo = () => {
        if (videoRef.current) {
            const playState = getVideoPlayState();

            if (playState !== VIDEO_PLAY_STATES.STOPPED) {
                pauseVideo();
                resetVideoToStart();
            }
        }
    };

    const endVideo = () => {
        if (videoRef.current) {
            const playState = getVideoPlayState();

            if (playState !== VIDEO_PLAY_STATES.ENDED) {
                pauseVideo();
                resetVideoToEnd();
            }
        }
    };

    const seekVideo = (time) => {
        if (videoRef.current) {
            videoRef.current.currentTime = time;
        }
    };

    const restartVideo = () => {
        if (videoRef.current) {
            resetVideoToStart();
            playVideo();
        }
    };

    const handleLoadedMetadata = () => resetVideoToStart();

    return {
        getVideoCurrentTime,
        getVideoEndTime,
        getVideoPlayState,
        resetVideoToStart,
        resetVideoToEnd,
        playVideo,
        pauseVideo,
        stopVideo,
        endVideo,
        seekVideo,
        restartVideo,
        handleLoadedMetadata,
    };
};

const handleGuideVideoUserControls = ({videoRef, videoControls}) => {
    const {getVideoPlayState, playVideo, pauseVideo} = videoControls;

    const handleControlsClick = () => {
        const video = videoRef.current;

        if (video) {
            const playState = getVideoPlayState();

            if (playState === VIDEO_PLAY_STATES.PLAYING) {
                pauseVideo();
            } else {
                playVideo();
            }
        }
    };

    return {handleControlsClick};
};

export {guideVideoUtils, handleGuideVideoUserControls};
